import React, { useState } from "react";
import { Breadcrumbs } from "#root/components/Breadcrumbs";
import { Button, Input, Field } from "@fluentui/react-components";
import {
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  createTableColumn,
  TableColumnDefinition,
} from "@fluentui/react-table";
import { EditFilled } from "@fluentui/react-icons";
import LoadingWrapper from "#root/components/LoadingWrapper";
import Paginator from "#root/components/Paginator";
import { showToast } from "#root/components/GlobalToaster";
import { DialogBox } from "#root/components/DialogBox";
import "./styles.scss";

import {
  useOrganizationRoles,
  useUpdateOrganizationRole,
  OrganizationRole,
} from "#root/queries/organizationRoles";

const RolesPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
  const [editRoleData, setEditRoleData] = useState<OrganizationRole | null>(null);

  const { data: rolesData, isLoading } = useOrganizationRoles({
    skip: (currentPage - 1) * itemsPerPage,
    take: itemsPerPage,
  });
  const roles: OrganizationRole[] =
    rolesData?.items.map((r) => ({ ...r, color: r.color?.trim() || "#000000" })) || [];
  const totalCount = rolesData?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const updateRoleMutation = useUpdateOrganizationRole();

  const handleEditClick = (role: OrganizationRole) => {
    setEditRoleData(role);
    setIsEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditRoleData(null);
  };

  const handleSaveRole = () => {
    if (!editRoleData) return;

    if (!editRoleData.letter) {
      showToast("Letter is required", "error");
      return;
    }
    if (!editRoleData.color) {
      showToast("Color is required", "error");
      return;
    }
    try {
      updateRoleMutation.mutate({ role: editRoleData });
      showToast('Role updated successfully', 'success');
      closeEditDialog();
    } catch (error) {
          showToast('Failed to update role', 'error');
    }
  };

  const columns: TableColumnDefinition<OrganizationRole>[] = [
    createTableColumn<OrganizationRole>({
      columnId: "role",
      renderHeaderCell: () => "Role",
      renderCell: (item) => item.role,
    }),
    createTableColumn<OrganizationRole>({
      columnId: "letter",
      renderHeaderCell: () => "Letter",
      renderCell: (item) => item.letter,
    }),
    createTableColumn<OrganizationRole>({
      columnId: "color",
      renderHeaderCell: () => "Color",
      renderCell: (item) => (
        <div className="role-color" style={{ backgroundColor: item.color }} />
      ),
    }),
    createTableColumn<OrganizationRole>({
      columnId: "action",
      renderHeaderCell: () => "Action",
      renderCell: (item) => (
        <div className="action-buttons">
          <Button
            icon={<EditFilled />}
            appearance="subtle"
            title="Edit Role"
            onClick={() => handleEditClick(item)}
          />
        </div>
      ),
    }),
  ];

  return (
    <div className="roles-page">
      <div className="emc-page-header">
        <div className="emc-page-header__top-row">
          <div className="emc-page-header__breadcrumbs">
            <Breadcrumbs />
          </div>
        </div>
        <div className="emc-page-header__title">
          <h2>
            Roles<div className="divider" />
          </h2>
        </div>
      </div>

      <LoadingWrapper
        isLoading={isLoading}
        isEmpty={roles.length === 0}
        emptyMessage="No roles found."
      >
        <div className="emc-grid">
          <DataGrid
            className="emc-datagrid"
            items={roles}
            columns={columns}
            getRowId={(item) => item.id}
            focusMode="none"
          >
            <DataGridHeader>
              <DataGridRow>
                {({ renderHeaderCell, columnId }) => (
                  <DataGridHeaderCell className={String(columnId)}>
                    {renderHeaderCell()}
                  </DataGridHeaderCell>
                )}
              </DataGridRow>
            </DataGridHeader>
            <DataGridBody<OrganizationRole>>
              {({ item, rowId }) => (
                <DataGridRow key={rowId}>
                  {({ renderCell, columnId }) => (
                    <DataGridCell className={String(columnId)}>
                      {renderCell(item)}
                    </DataGridCell>
                  )}
                </DataGridRow>
              )}
            </DataGridBody>
          </DataGrid>

          <Paginator
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
            totalItems={totalCount}
            currentItems={roles.length}
            showItemsPerPage={true}
          />
        </div>
      </LoadingWrapper>

      <DialogBox
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        title="Edit Role"
        primaryButtonText="Save"
        onPrimaryButtonClick={handleSaveRole}
        secondaryButtonText="Cancel"
        onSecondaryButtonClick={closeEditDialog}
      >
        {editRoleData && (
          <form>
            <Field label="Role">
              <Input value={editRoleData.role} readOnly />
            </Field>
            <div className="field-two-columns">
              <Field label="Letter" required>
                <Input
                  value={editRoleData.letter}
                  maxLength={1}
                  onChange={(e, data) => {
                    const letter = data.value.replace(/[^a-zA-Z]/g, '').slice(0, 1);
                    setEditRoleData({ ...editRoleData, letter });
                  }}
                />
              </Field>
              <Field label="Color" required>
                <input
                  type="color"
                  value={editRoleData.color}
                  onChange={(e) =>
                    setEditRoleData({ ...editRoleData, color: e.target.value })
                  }
                  className="color-picker"
                />
              </Field>
            </div>
          </form>
        )}
      </DialogBox>
    </div>
  );
};

export default RolesPage;
